import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Platform = "android" | "ios" | "desktop" | "unknown";
type InApp = "telegram" | "instagram" | "facebook" | "vk" | "yandex" | "other" | "none";

const LS = {
  dismissedAt: "pwaInstall.dismissedAt",
  promptCount: "pwaInstall.promptCount",
  installed: "pwaInstall.installed",
};

export type UsePwaInstallOptions = {
  cooldownDays?: number;
  maxPrompts?: number;
};

function daysDiff(fromISO: string) {
  const from = new Date(fromISO).getTime();
  return Math.floor((Date.now() - from) / 86400000);
}

function isStandalone(): boolean {
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari (у некоторых браузеров есть navigator.standalone)
  if ((window.navigator as any)?.standalone) return true;
  return false;
}

function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  if (/mac|win|linux/.test(ua)) return "desktop";
  return "unknown";
}

function detectInApp(): InApp {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("telegram")) return "telegram";
  if (ua.includes("instagram")) return "instagram";
  if (ua.includes("fbav") || ua.includes("facebook")) return "facebook";
  if (ua.includes("vk")) return "vk";
  if (ua.includes("yainapp") || ua.includes("yandexsearch")) return "yandex";
  return "none";
}

export function usePwaInstall(opts: UsePwaInstallOptions = {}) {
  const { cooldownDays = 7, maxPrompts = 3 } = opts;

  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [installed, setInstalled] = useState<boolean>(isStandalone());
  const [supported, setSupported] = useState<boolean>(false);
  const [showIosGuide, setShowIosGuide] = useState<boolean>(false);

  const platform = useMemo(detectPlatform, []);
  const inApp = useMemo(detectInApp, []);
  const promptBlocked = inApp !== "none";

  const promptCountRef = useRef<number>(Number(localStorage.getItem(LS.promptCount) || "0"));

  useEffect(() => {
    if (installed) localStorage.setItem(LS.installed, "1");
  }, [installed]);

  useEffect(() => {
    const onBip = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setSupported(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (installed) return;
    if (platform === "ios") {
      setSupported(false);
      setShowIosGuide(true);
    }
  }, [platform, installed]);

  const canShowNow = useMemo(() => {
    if (installed) return false;
    if (promptBlocked) return true;
    const dismissedAt = localStorage.getItem(LS.dismissedAt);
    const count = promptCountRef.current;
    if (count >= maxPrompts) return false;
    if (!dismissedAt) return true;
    return daysDiff(dismissedAt) >= cooldownDays;
  }, [installed, promptBlocked, cooldownDays, maxPrompts]);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    promptCountRef.current += 1;
    localStorage.setItem(LS.promptCount, String(promptCountRef.current));
    return outcome === "accepted";
  }, [deferredPrompt]);

  const markLater = useCallback(() => {
    localStorage.setItem(LS.dismissedAt, new Date().toISOString());
    promptCountRef.current += 1;
    localStorage.setItem(LS.promptCount, String(promptCountRef.current));
  }, []);

  return {
    installed,
    supported,
    showIosGuide,
    platform,
    inApp,
    canShowNow,
    promptInstall,
    markLater,
  };
}
